import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { formatDateNL } from '../utils/dateFormat';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent } from './ui/card';
import { Check, X, FileText, AlertCircle, Pen, Loader2, Download, Euro, Camera, Image as ImageIcon, MessageSquare, Send, ChevronLeft, ChevronRight, Mail } from 'lucide-react';
import { PaymentPlanBlock } from './PaymentPlan';
import { StandardTermsBlock } from './StandardTerms';
import { toast } from 'sonner';
import html2pdf from 'html2pdf.js';
import { cldThumb, cldFull } from '../lib/cloudinary';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmtEuro = (v) => `€ ${(Number(v) || 0).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const COMPANY = {
  name: 'Cardoso Bouw en Onderhoud',
  address: 'Vuntuslaan 69',
  city: '1231 NR Loosdrecht',
  phone: '0685071009',
  email: 'info@cardosobouw.nl',
  kvk: '85310646',
  btw_id: 'NL004077138B58',
  iban: 'NL33INGB0709828160',
  bic: 'INGBNL2A',
};

// Inline lightbox — keyboard nav + portal so it always overlays correctly on iOS
const PhotoLightbox = ({ photos, index, onClose, onPrev, onNext }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft') onPrev();
      else if (e.key === 'ArrowRight') onNext();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose, onPrev, onNext]);

  if (index < 0 || index >= photos.length) return null;
  const p = photos[index];
  const url = cldFull(p.cloudinary_url || p.url);

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center"
      onClick={onClose}
      data-testid="public-photo-lightbox"
    >
      <button
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        className="absolute top-4 right-4 text-white hover:text-gray-300 bg-black/40 rounded-full p-2"
        data-testid="lightbox-close"
        aria-label="Sluiten"
      >
        <X className="w-6 h-6" />
      </button>
      {photos.length > 1 && (
        <>
          <button
            onClick={(e) => { e.stopPropagation(); onPrev(); }}
            className="absolute left-4 text-white hover:text-gray-300 bg-black/40 rounded-full p-2"
            data-testid="lightbox-prev"
            aria-label="Vorige"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); onNext(); }}
            className="absolute right-4 text-white hover:text-gray-300 bg-black/40 rounded-full p-2"
            data-testid="lightbox-next"
            aria-label="Volgende"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        </>
      )}
      <div
        className="max-w-[92vw] max-h-[88vh] flex flex-col items-center"
        onClick={(e) => e.stopPropagation()}
      >
        <img
          src={url}
          alt={p.filename || 'foto'}
          className="max-w-full max-h-[80vh] object-contain rounded shadow-2xl"
        />
        <div className="mt-3 text-white/80 text-xs flex items-center gap-3">
          <span data-testid="lightbox-counter">{index + 1} / {photos.length}</span>
          {p.entry_date && <span>· {p.entry_date}</span>}
          {p.project && <span>· {p.project}</span>}
        </div>
      </div>
    </div>,
    document.body
  );
};

// Lightweight signature canvas (no external dep)
const SignatureCanvas = ({ onChange }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const dirty = useRef(false);

  const point = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    if (e.touches && e.touches[0]) {
      return { x: e.touches[0].clientX - rect.left, y: e.touches[0].clientY - rect.top };
    }
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = point(e);
  };
  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const c = canvasRef.current;
    const ctx = c.getContext('2d');
    const p = point(e);
    ctx.strokeStyle = '#1a3650';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    dirty.current = true;
  };
  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (onChange && dirty.current) {
      onChange(canvasRef.current.toDataURL('image/png'));
    }
  };
  const clear = () => {
    const c = canvasRef.current;
    c.getContext('2d').clearRect(0, 0, c.width, c.height);
    dirty.current = false;
    if (onChange) onChange('');
  };

  // Setup high-DPI canvas
  useEffect(() => {
    const c = canvasRef.current;
    const ratio = window.devicePixelRatio || 1;
    c.width = c.offsetWidth * ratio;
    c.height = c.offsetHeight * ratio;
    c.getContext('2d').scale(ratio, ratio);
  }, []);

  return (
    <div>
      <canvas
        ref={canvasRef}
        className="w-full h-40 bg-white border-2 border-gray-300 rounded-lg cursor-crosshair touch-none"
        onMouseDown={start} onMouseMove={move} onMouseUp={end} onMouseLeave={end}
        onTouchStart={start} onTouchMove={move} onTouchEnd={end}
        data-testid="signature-canvas"
      />
      <div className="flex justify-between mt-1">
        <p className="text-xs text-gray-500 flex items-center gap-1"><Pen className="w-3 h-3" />Teken hier uw handtekening</p>
        <button type="button" onClick={clear} className="text-xs text-gray-500 hover:text-red-500 underline">Wissen</button>
      </div>
    </div>
  );
};

const PublicQuote = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const fromPortal = searchParams.get('from') === 'portal';
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mode, setMode] = useState(null); // null | 'approve' | 'reject' | 'done'
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);
  const [paymentStatus, setPaymentStatus] = useState(null); // {status, checkout_url, down_payment_amount, ...}
  const [payLoading, setPayLoading] = useState(false);
  const [bundle, setBundle] = useState({ photos_company: [], photos_client: [], work_request: null });
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [commentAuthor, setCommentAuthor] = useState('');
  const [postingComment, setPostingComment] = useState(false);
  const [lightbox, setLightbox] = useState({ open: false, photos: [], index: 0 });
  const [emailLoading, setEmailLoading] = useState(false);
  const pdfRef = useRef(null);

  // Approve form
  const [signerName, setSignerName] = useState('');
  const [signerAddress, setSignerAddress] = useState('');
  const [signerPhone, setSignerPhone] = useState('');
  const [signatureData, setSignatureData] = useState('');

  // Reject form
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    const load = async () => {
      try {
        const r = await axios.get(`${API}/public/quotes/${token}`);
        setQuote(r.data);
        // Prefill from quote
        if (r.data.client_name) {
          setSignerName(r.data.client_name);
          setCommentAuthor(r.data.client_name);
        }
        if (r.data.client_address) {
          const full = [r.data.client_address, r.data.client_city].filter(Boolean).join(', ');
          setSignerAddress(full);
        }
        if (r.data.client_phone) setSignerPhone(r.data.client_phone);
        // If quote requires aanbetaling, fetch latest payment status
        if (r.data.down_payment_percentage > 0 || searchParams.get('paid')) {
          try {
            const ps = await axios.get(`${API}/public/quotes/${token}/payment-status`);
            setPaymentStatus(ps.data);
          } catch (_) {}
        }
        // Photos + work request bundle + comments — failures here shouldn't break the page
        try {
          const [bRes, cRes] = await Promise.all([
            axios.get(`${API}/public/quotes/${token}/bundle`),
            axios.get(`${API}/public/quotes/${token}/comments`),
          ]);
          setBundle(bRes.data || { photos_company: [], photos_client: [], work_request: null });
          setComments(cRes.data || []);
        } catch (_) {}
      } catch (e) {
        setError(e.response?.data?.detail || 'Offerte niet gevonden of link verlopen');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [token, searchParams]);

  const refreshComments = async () => {
    try {
      const r = await axios.get(`${API}/public/quotes/${token}/comments`);
      setComments(r.data || []);
    } catch (_) {}
  };

  const handlePostComment = async () => {
    if (!newComment.trim()) {
      toast.error('Schrijf eerst een bericht');
      return;
    }
    setPostingComment(true);
    try {
      await axios.post(`${API}/public/quotes/${token}/comments`, {
        body: newComment.trim(),
        author_name: commentAuthor.trim() || quote?.client_name || 'Klant',
      });
      setNewComment('');
      toast.success('Bericht verzonden');
      await refreshComments();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setPostingComment(false);
    }
  };

  const openLightbox = (photos, index) => setLightbox({ open: true, photos, index });
  const closeLightbox = () => setLightbox(s => ({ ...s, open: false }));
  const nextPhoto = () => setLightbox(s => ({ ...s, index: (s.index + 1) % s.photos.length }));
  const prevPhoto = () => setLightbox(s => ({ ...s, index: (s.index - 1 + s.photos.length) % s.photos.length }));

  const handleDownloadPdf = () => {
    if (!pdfRef.current || !quote) {
      toast.error('PDF nog niet beschikbaar');
      return;
    }
    const filename = `Offerte_${quote.number}_${(quote.client_name || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
    const opts = {
      margin: 0,
      filename,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'], before: '.html2pdf__page-break' },
    };
    html2pdf().set(opts).from(pdfRef.current).save()
      .then(() => toast.success('PDF gedownload'))
      .catch(() => toast.error('Fout bij downloaden PDF'));
  };

  const handleEmailToSelf = async () => {
    if (!pdfRef.current || !quote) {
      toast.error('PDF nog niet beschikbaar');
      return;
    }
    if (!quote.client_email) {
      toast.error('Er staat geen e-mailadres op deze offerte — neem contact met ons op.');
      return;
    }
    setEmailLoading(true);
    try {
      const filename = `Offerte_${quote.number}_${(quote.client_name || '').replace(/[^a-z0-9]/gi, '_')}.pdf`;
      const opts = {
        margin: 0,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
        pagebreak: { mode: ['css', 'legacy'], before: '.html2pdf__page-break' },
      };
      const pdfDataUri = await html2pdf().set(opts).from(pdfRef.current).outputPdf('datauristring');
      await axios.post(`${API}/public/quotes/${token}/email-to-self`, {
        pdf_base64: pdfDataUri,
        pdf_filename: filename,
      });
      toast.success(`Offerte verzonden naar ${quote.client_email}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Versturen mislukt');
    } finally {
      setEmailLoading(false);
    }
  };

  const handleStartPayment = async () => {
    setPayLoading(true);
    try {
      const r = await axios.post(`${API}/public/quotes/${token}/down-payment`, {});
      if (r.data.checkout_url) {
        window.location.href = r.data.checkout_url;
      } else {
        toast.error('Geen betaal-link ontvangen');
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Aanbetaling aanmaken mislukt');
    } finally {
      setPayLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!signerName.trim() || !signerAddress.trim() || !signerPhone.trim()) {
      toast.error('Vul uw naam, adres en telefoonnummer in');
      return;
    }
    if (!signatureData) {
      toast.error('Plaats uw handtekening hieronder');
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/quotes/${token}/respond`, {
        action: 'approve',
        signer_name: signerName,
        signer_address: signerAddress,
        signer_phone: signerPhone,
        signature_data: signatureData,
      });
      setDone('approve');
      setMode('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Er ging iets mis. Probeer opnieuw.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReject = async () => {
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/quotes/${token}/respond`, {
        action: 'reject',
        rejection_reason: rejectionReason,
        signer_name: signerName || quote?.client_name || '',
      });
      setDone('reject');
      setMode('done');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Er ging iets mis');
    } finally {
      setSubmitting(false);
    }
  };

  // Reusable extras block — werkaanvraag context + foto's + reacties
  const renderExtras = () => {
    const wr = bundle.work_request;
    const hasCompanyPhotos = (bundle.photos_company || []).length > 0;
    const hasClientPhotos = (bundle.photos_client || []).length > 0;
    return (
      <>
        {wr && (
          <Card data-testid="public-work-request-block">
            <CardContent className="p-6 space-y-2">
              <h3 className="text-lg font-bold text-[#1a5276] flex items-center gap-2">
                <FileText className="w-5 h-5" /> Aanvraag: {wr.title || 'Werkzaamheden'}
              </h3>
              {wr.description && <p className="text-sm text-gray-700 whitespace-pre-wrap">{wr.description}</p>}
              {wr.address && <p className="text-xs text-gray-500">Adres: {wr.address}</p>}
              {wr.preferred_start_date && (
                <p className="text-xs text-gray-500">
                  Gewenste startdatum: {formatDateNL(wr.preferred_start_date)}
                </p>
              )}
              {Array.isArray(wr.photos) && wr.photos.length > 0 && (
                <div className="grid grid-cols-3 md:grid-cols-5 gap-2 pt-2">
                  {wr.photos.map((p, i) => (
                    <img
                      key={i}
                      src={p.file_data ? `data:${p.file_type};base64,${p.file_data}` : (p.cloudinary_url || p.url)}
                      alt={p.filename || `aanvraag-${i}`}
                      className="w-full h-24 object-cover rounded border border-gray-200"
                      loading="lazy"
                      data-testid={`public-wr-photo-${i}`}
                    />
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {(hasCompanyPhotos || hasClientPhotos) && (
          <Card data-testid="public-photos-block">
            <CardContent className="p-6 space-y-5">
              <h3 className="text-lg font-bold text-[#1a5276] flex items-center gap-2">
                <Camera className="w-5 h-5" /> Foto's van het werk
              </h3>

              {hasCompanyPhotos && (
                <div data-testid="public-company-photos">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <Camera className="w-4 h-4 text-[#3da5a5]" /> Door Cardoso Bouw ({bundle.photos_company.length})
                  </h4>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {bundle.photos_company.map((p, i) => (
                      <button
                        key={p.id || i}
                        type="button"
                        onClick={() => openLightbox(bundle.photos_company, i)}
                        className="aspect-square block rounded overflow-hidden bg-gray-100 border border-gray-200 hover:opacity-80 transition focus:outline-none focus:ring-2 focus:ring-[#3da5a5]"
                        data-testid={`public-company-photo-${i}`}
                      >
                        <img
                          src={cldThumb(p.cloudinary_thumbnail || p.cloudinary_url, 400)}
                          alt={p.project || 'foto'}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {hasClientPhotos && (
                <div data-testid="public-client-photos">
                  <h4 className="text-sm font-semibold text-gray-700 mb-2 flex items-center gap-2">
                    <ImageIcon className="w-4 h-4 text-amber-500" /> Door u geüpload ({bundle.photos_client.length})
                  </h4>
                  <div className="grid grid-cols-3 md:grid-cols-5 gap-2">
                    {bundle.photos_client.map((p, i) => (
                      <button
                        key={p.id || i}
                        type="button"
                        onClick={() => openLightbox(bundle.photos_client, i)}
                        className="aspect-square block rounded overflow-hidden bg-amber-50 border border-amber-200 hover:opacity-80 transition focus:outline-none focus:ring-2 focus:ring-amber-400"
                        data-testid={`public-client-photo-${i}`}
                      >
                        <img
                          src={cldThumb(p.cloudinary_thumbnail || p.cloudinary_url, 400)}
                          alt={p.filename || 'mijn foto'}
                          loading="lazy"
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        <Card data-testid="public-comments-block">
          <CardContent className="p-5 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div className="text-center sm:text-left">
              <h3 className="text-base font-bold text-[#1a5276] flex items-center gap-2 justify-center sm:justify-start">
                <MessageSquare className="w-5 h-5" /> Heeft u vragen?
              </h3>
              <p className="text-sm text-gray-600 mt-0.5">
                Neem direct contact met ons op via WhatsApp — wij reageren snel.
              </p>
            </div>
            <a
              href="https://wa.me/31685071009"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#20bd5a] text-white px-5 py-2.5 rounded-md font-semibold whitespace-nowrap transition-colors"
              data-testid="whatsapp-contact-btn"
            >
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-current" aria-hidden="true">
                <path d="M.057 24l1.687-6.163a11.867 11.867 0 01-1.587-5.946C.16 5.335 5.495 0 12.05 0a11.817 11.817 0 018.413 3.488 11.824 11.824 0 013.48 8.414c-.003 6.557-5.338 11.892-11.893 11.892a11.9 11.9 0 01-5.688-1.448L.057 24zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-5.462-4.415-9.89-9.881-9.892-5.452 0-9.887 4.434-9.889 9.885a9.86 9.86 0 001.575 5.323l-1.001 3.661 3.915-.684zm6.566-5.444c-.299-.149-1.758-.867-2.031-.967-.272-.099-.47-.149-.669.15-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.521.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347z" />
              </svg>
              WhatsApp ons
            </a>
          </CardContent>
        </Card>

        {lightbox.open && (
          <PhotoLightbox
            photos={lightbox.photos}
            index={lightbox.index}
            onClose={closeLightbox}
            onPrev={prevPhoto}
            onNext={nextPhoto}
          />
        )}
      </>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <Loader2 className="w-10 h-10 animate-spin text-[#3da5a5]" />
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-6">
        <Card className="max-w-md w-full">
          <CardContent className="py-10 text-center space-y-3">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
            <h2 className="text-xl font-bold text-gray-800">{error}</h2>
            <p className="text-sm text-gray-500">Neem contact op met Cardoso Bouw voor een nieuwe link.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Already responded — show simplified confirmation view ONLY for the public
  // email-link audience. Logged-in klanten (fromPortal=true) get the full
  // offerte view below (with line items, totals, PDF download) plus a status
  // banner — so they can review and download their own document.
  if (quote && (quote.status === 'goedgekeurd' || quote.status === 'afgewezen') && mode !== 'done' && !fromPortal) {
    const cr = quote.client_response || {};
    const showPayBlock = quote.status === 'goedgekeurd' && (quote.down_payment_percentage || 0) > 0;
    const ps = paymentStatus || {};
    const dpLabel = quote.down_payment_label || 'Aanbetaling';
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-2xl mx-auto space-y-4">
          {fromPortal && (
            <button
              onClick={() => navigate('/client/dashboard')}
              className="flex items-center gap-2 text-sm text-[#1a5276] hover:text-[#3da5a5]"
              data-testid="back-to-portal-responded"
            >
              <ChevronLeft className="w-4 h-4" /> Terug naar mijn portaal
            </button>
          )}
          <Card className="border-2 border-gray-200">
            <CardContent className="py-10 text-center space-y-3">
              {quote.status === 'goedgekeurd' ? (
                <>
                  <div className="w-16 h-16 bg-green-100 rounded-full mx-auto flex items-center justify-center">
                    <Check className="w-10 h-10 text-green-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Offerte {quote.number} reeds goedgekeurd</h2>
                  <p className="text-gray-600">Getekend door {cr.signer_name || 'klant'}</p>
                  <p className="text-sm text-gray-400">{new Date(cr.responded_at).toLocaleString('nl-NL')}</p>
                </>
              ) : (
                <>
                  <div className="w-16 h-16 bg-red-100 rounded-full mx-auto flex items-center justify-center">
                    <X className="w-10 h-10 text-red-600" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800">Offerte {quote.number} is afgewezen</h2>
                  <p className="text-sm text-gray-400">{new Date(cr.responded_at).toLocaleString('nl-NL')}</p>
                </>
              )}
            </CardContent>
          </Card>

          {showPayBlock && (
            <Card className="border-2 border-[#3da5a5]" data-testid="payment-status-block">
              <CardContent className="py-8 space-y-4">
                <h3 className="text-lg font-bold text-[#1a5276] flex items-center gap-2">
                  <Euro className="w-5 h-5" /> {dpLabel}
                </h3>
                <p className="text-sm text-gray-700">
                  {dpLabel} van <strong>{ps.down_payment_percentage || quote.down_payment_percentage}%</strong> van het totaalbedrag
                  {ps.down_payment_amount && <> — <strong>{fmtEuro(ps.down_payment_amount)}</strong></>}.
                </p>
                {ps.status === 'paid' ? (
                  <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800 flex items-center gap-2">
                    <Check className="w-5 h-5" /> {dpLabel} voldaan {ps.paid_at && <span className="text-xs text-green-600">({new Date(ps.paid_at).toLocaleString('nl-NL')})</span>}
                  </div>
                ) : ps.status === 'open' ? (
                  <>
                    <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3">
                      Uw betaling staat klaar maar is nog niet voltooid. Klik hieronder om te betalen.
                    </p>
                    <Button onClick={handleStartPayment} disabled={payLoading} className="bg-[#3da5a5] hover:bg-[#2d8585] w-full" data-testid="pay-now-btn">
                      {payLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Bezig...</> : <><Euro className="w-4 h-4 mr-2" /> {dpLabel} voltooien</>}
                    </Button>
                  </>
                ) : ps.status === 'expired' || ps.status === 'canceled' || ps.status === 'failed' ? (
                  <>
                    <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                      Vorige betaling is {ps.status === 'expired' ? 'verlopen' : ps.status === 'canceled' ? 'geannuleerd' : 'mislukt'}. U kunt opnieuw proberen.
                    </p>
                    <Button onClick={handleStartPayment} disabled={payLoading} className="bg-[#3da5a5] hover:bg-[#2d8585] w-full" data-testid="pay-retry-btn">
                      {payLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Bezig...</> : <><Euro className="w-4 h-4 mr-2" /> Opnieuw betalen</>}
                    </Button>
                  </>
                ) : (
                  <Button onClick={handleStartPayment} disabled={payLoading} className="bg-[#3da5a5] hover:bg-[#2d8585] w-full" data-testid="pay-start-btn">
                    {payLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Bezig...</> : <><Euro className="w-4 h-4 mr-2" /> {dpLabel} betalen</>}
                  </Button>
                )}
              </CardContent>
            </Card>
          )}

          {showPayBlock && ps.status === 'paid' && (
            <Card className="border-2 border-[#3da5a5] bg-gradient-to-br from-[#3da5a5]/5 to-white" data-testid="register-after-payment-block">
              <CardContent className="py-8 text-center space-y-4">
                <div className="w-14 h-14 mx-auto bg-[#3da5a5]/10 rounded-full flex items-center justify-center">
                  <Check className="w-8 h-8 text-[#3da5a5]" />
                </div>
                <h3 className="text-xl font-bold text-gray-800">Bedankt voor uw betaling!</h3>
                <p className="text-sm text-gray-600">
                  Wilt u uw werkzaamheden eenvoudig kunnen volgen? Maak een gratis account aan om foto's, voortgang, offertes en facturen op één plek te bekijken.
                </p>
                <Button
                  onClick={() => navigate(`/client/register?email=${encodeURIComponent(quote?.client_email||'')}&phone=${encodeURIComponent(quote?.client_phone||'')}&name=${encodeURIComponent(quote?.client_name||'')}`)}
                  className="bg-[#3da5a5] hover:bg-[#2d8585] w-full sm:w-auto"
                  data-testid="register-after-payment-btn"
                >
                  Gratis account aanmaken
                </Button>
                <p className="text-xs text-gray-400">
                  Al een account? <a href="/client" className="text-[#3da5a5] underline">Inloggen via dashboard.cardosobouw.nl/client</a>
                </p>
              </CardContent>
            </Card>
          )}

          {renderExtras()}
        </div>
      </div>
    );
  }

  // Success page
  if (mode === 'done') {
    const isApprove = done === 'approve';
    const needsPayment = isApprove && quote && (quote.down_payment_percentage || 0) > 0;
    const dpLabel = quote?.down_payment_label || 'Aanbetaling';
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#3da5a5]/10 to-white p-6 flex items-center justify-center">
        <Card className="max-w-xl w-full shadow-lg">
          <CardContent className="py-12 text-center space-y-5">
            <div className={`w-20 h-20 mx-auto rounded-full flex items-center justify-center ${isApprove ? 'bg-green-100' : 'bg-orange-100'}`}>
              {isApprove ? <Check className="w-12 h-12 text-green-600" /> : <X className="w-12 h-12 text-orange-600" />}
            </div>
            <h2 className="text-3xl font-bold text-gray-800">
              {isApprove ? 'Bedankt voor uw goedkeuring!' : 'Bedankt voor uw reactie'}
            </h2>
            {isApprove ? (
              <p className="text-gray-600">
                We nemen zo spoedig mogelijk contact met u op om de werkzaamheden in te plannen.
              </p>
            ) : (
              <p className="text-orange-700 font-medium text-lg">
                💬 We denken graag met u mee voor een aangepaste offerte!
              </p>
            )}

            {needsPayment && (
              <div className="bg-[#3da5a5]/10 border-2 border-[#3da5a5] rounded-lg p-4 space-y-3" data-testid="post-approve-payment">
                <div className="flex items-center justify-center gap-2 text-[#1a5276] font-semibold">
                  <Euro className="w-5 h-5" /> {dpLabel} van {quote.down_payment_percentage}% vereist
                </div>
                <p className="text-sm text-gray-700">
                  Om uw opdracht te bevestigen vragen wij een {dpLabel.toLowerCase()}. Klik hieronder om veilig via Mollie te betalen (iDEAL, Bancontact, creditcard, PayPal).
                </p>
                <Button onClick={handleStartPayment} disabled={payLoading} className="bg-[#3da5a5] hover:bg-[#2d8585] w-full h-12" data-testid="post-approve-pay-btn">
                  {payLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Bezig...</> : <><Euro className="w-4 h-4 mr-2" /> {dpLabel} betalen</>}
                </Button>
              </div>
            )}

            <div className="pt-4 border-t border-gray-200 space-y-2">
              <p className="text-sm text-gray-600">Wilt u toegang tot al uw informatie?</p>
              <p className="text-xs text-gray-500">Foto's, offertes, facturen — alles op één plek.</p>
              <Button
                onClick={() => navigate(`/client/register?email=${encodeURIComponent(quote?.client_email||'')}&phone=${encodeURIComponent(signerPhone||quote?.client_phone||'')}&name=${encodeURIComponent(signerName||quote?.client_name||'')}`)}
                className="bg-[#3da5a5] hover:bg-[#2d8585] mt-2"
                data-testid="register-cta-btn"
              >
                Gratis account aanmaken
              </Button>
            </div>
            <p className="text-xs text-gray-400 pt-4">Cardoso Bouw en Onderhoud</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto space-y-6">
        {fromPortal && (
          <button
            onClick={() => navigate('/client/dashboard')}
            className="flex items-center gap-2 text-sm text-[#1a5276] hover:text-[#3da5a5] mb-2"
            data-testid="back-to-portal"
          >
            <ChevronLeft className="w-4 h-4" /> Terug naar mijn portaal
          </button>
        )}

        {/* Status banner — shown when klant opens an already-responded offerte from their portal */}
        {fromPortal && quote && (quote.status === 'goedgekeurd' || quote.status === 'afgewezen') && (
          <Card
            className={`border-2 ${quote.status === 'goedgekeurd' ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}
            data-testid="portal-quote-status-banner"
          >
            <CardContent className="py-4 px-5 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${quote.status === 'goedgekeurd' ? 'bg-green-100' : 'bg-red-100'}`}>
                {quote.status === 'goedgekeurd'
                  ? <Check className="w-6 h-6 text-green-600" />
                  : <X className="w-6 h-6 text-red-600" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-bold ${quote.status === 'goedgekeurd' ? 'text-green-800' : 'text-red-800'}`}>
                  {quote.status === 'goedgekeurd'
                    ? `Offerte ${quote.number} reeds goedgekeurd`
                    : `Offerte ${quote.number} is afgewezen`}
                </p>
                {quote.client_response?.responded_at && (
                  <p className="text-xs text-gray-600">
                    {quote.client_response.signer_name && `Door ${quote.client_response.signer_name} · `}
                    {new Date(quote.client_response.responded_at).toLocaleString('nl-NL')}
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Aanbetaling status (logged-in klant view of an already-responded offerte with down-payment) */}
        {fromPortal && quote && quote.status === 'goedgekeurd' && (quote.down_payment_percentage || 0) > 0 && (() => {
          const dpLabel = quote.down_payment_label || 'Aanbetaling';
          return (
          <Card className="border-2 border-[#3da5a5]" data-testid="payment-status-block-portal">
            <CardContent className="py-5 px-5 space-y-3">
              <h3 className="text-base font-bold text-[#1a5276] flex items-center gap-2">
                <Euro className="w-4 h-4" /> {dpLabel}
              </h3>
              <p className="text-sm text-gray-700">
                {dpLabel} van <strong>{(paymentStatus && paymentStatus.down_payment_percentage) || quote.down_payment_percentage}%</strong> van het totaalbedrag
                {paymentStatus?.down_payment_amount && <> — <strong>{fmtEuro(paymentStatus.down_payment_amount)}</strong></>}.
              </p>
              {paymentStatus?.status === 'paid' ? (
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800 flex items-center gap-2">
                  <Check className="w-5 h-5" /> {dpLabel} voldaan {paymentStatus.paid_at && <span className="text-xs text-green-600">({new Date(paymentStatus.paid_at).toLocaleString('nl-NL')})</span>}
                </div>
              ) : paymentStatus?.status === 'open' ? (
                <>
                  <p className="text-sm text-orange-700 bg-orange-50 border border-orange-200 rounded p-3">
                    Uw betaling staat klaar maar is nog niet voltooid. Klik hieronder om te betalen.
                  </p>
                  <Button onClick={handleStartPayment} disabled={payLoading} className="bg-[#3da5a5] hover:bg-[#2d8585] w-full" data-testid="pay-now-btn-portal">
                    {payLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Bezig...</> : <><Euro className="w-4 h-4 mr-2" /> {dpLabel} voltooien</>}
                  </Button>
                </>
              ) : (paymentStatus?.status === 'expired' || paymentStatus?.status === 'canceled' || paymentStatus?.status === 'failed') ? (
                <>
                  <p className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-3">
                    Vorige betaling is {paymentStatus.status === 'expired' ? 'verlopen' : paymentStatus.status === 'canceled' ? 'geannuleerd' : 'mislukt'}. U kunt opnieuw proberen.
                  </p>
                  <Button onClick={handleStartPayment} disabled={payLoading} className="bg-[#3da5a5] hover:bg-[#2d8585] w-full" data-testid="pay-retry-btn-portal">
                    {payLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Bezig...</> : <><Euro className="w-4 h-4 mr-2" /> Opnieuw betalen</>}
                  </Button>
                </>
              ) : (
                <Button onClick={handleStartPayment} disabled={payLoading} className="bg-[#3da5a5] hover:bg-[#2d8585] w-full" data-testid="pay-start-btn-portal">
                  {payLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Bezig...</> : <><Euro className="w-4 h-4 mr-2" /> {dpLabel} betalen</>}
                </Button>
              )}
            </CardContent>
          </Card>
          );
        })()}

        {/* PDF-printable container — uses the same inline-style template as the
            admin /quotes PDF so the klant downloads the same document as the admin. */}
        <div ref={pdfRef}>
          <div style={{ fontFamily: 'Arial, sans-serif', padding: '40px', maxWidth: '800px', margin: '0 auto', color: '#333', background: 'white' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '30px' }}>
              <div>
                <h2 style={{ color: '#1a5276', fontSize: '22px', margin: '0 0 20px 0' }}>Offerte {quote.number}</h2>
                <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Naam Klant:</strong> {quote.client_name}</p>
                {quote.client_address && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Adres Klant:</strong> {quote.client_address}</p>}
                {quote.client_city && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Plaats Klant:</strong> {quote.client_city}</p>}
                {quote.client_email && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>E-mail:</strong> {quote.client_email}</p>}
                {quote.client_phone && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Telefoon:</strong> {quote.client_phone}</p>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <img src="/logo-cardoso.jpeg" alt="Logo" style={{ width: '120px', marginBottom: '10px' }} onError={(e) => { e.target.style.display = 'none'; }} />
                <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.name}</p>
                <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.address}</p>
                <p style={{ margin: '2px 0', fontSize: '12px' }}>{COMPANY.city}</p>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '20px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
              <div>{quote.description && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Omschrijving offerte:</strong> {quote.description}</p>}</div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Offertedatum:</strong> {quote.offer_date}</p>
                {quote.expiry_date && <p style={{ margin: '2px 0', fontSize: '13px' }}><strong>Vervaldatum:</strong> {quote.expiry_date}</p>}
              </div>
            </div>

            {Array.isArray(quote.previous_quotes_chain) && quote.previous_quotes_chain.length > 0 && (
              <div style={{ marginBottom: '18px', padding: '12px 14px', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '4px', fontSize: '12px' }}>
                <p style={{ margin: '0 0 6px 0', fontWeight: 'bold', color: '#065f46', fontSize: '13px' }}>✓ Reeds voldane termijnen</p>
                {quote.previous_quotes_chain.map((p, i) => (
                  <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', color: '#065f46' }}>
                    <span>{p.label || 'Aanbetaling'} — offerte {p.quote_number}{p.percentage ? ` (${p.percentage}%)` : ''}{p.paid_at ? ` · betaald ${new Date(p.paid_at).toLocaleDateString('nl-NL')}` : ''}</span>
                    {p.amount && <strong>€ {Number(p.amount).toLocaleString('nl-NL', { minimumFractionDigits: 2 })}</strong>}
                  </div>
                ))}
              </div>
            )}

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', marginBottom: '20px' }}>
              <thead><tr style={{ background: '#1a5276', color: 'white' }}>
                <th style={{ padding: '8px', textAlign: 'left' }}>Omschrijving</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Aantal</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Bedrag</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Korting</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>BTW</th>
                <th style={{ padding: '8px', textAlign: 'right' }}>Totaal excl. BTW</th>
              </tr></thead>
              <tbody>
                {(quote.items || []).map((item, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px' }}>{item.description}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{Number(item.quantity).toFixed(2).replace('.', ',')}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{fmtEuro(item.unit_price)}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{item.discount > 0 ? `${Number(item.discount).toFixed(2).replace('.', ',')}%` : ''}</td>
                    <td style={{ padding: '8px', textAlign: 'right' }}>{item.btw}%</td>
                    <td style={{ padding: '8px', textAlign: 'right', fontWeight: 'bold' }}>{fmtEuro(item.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {(() => { const isVerlegd = (quote.btw_status || '').toLowerCase() === 'verlegd'; return (
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '20px' }}>
              <div style={{ width: '280px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}><span>Subtotaal (excl. BTW)</span><strong>{fmtEuro(quote.subtotal)}</strong></div>
                {!isVerlegd && Object.entries(quote.btw_totals || {}).map(([rate, amt]) => (
                  <div key={rate} style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px' }}><span>BTW {rate}</span><span>{fmtEuro(amt)}</span></div>
                ))}
                {isVerlegd && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '12px', color: '#92400e', fontStyle: 'italic' }}><span>BTW verlegd</span><span>€ 0,00</span></div>
                )}
                {quote.general_discount > 0 && <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', fontSize: '13px', color: '#e74c3c' }}><span>Korting</span><span>-{fmtEuro(quote.general_discount)}</span></div>}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: '2px solid #1a5276', fontSize: '16px', fontWeight: 'bold', color: '#1a5276' }}><span>Totaal{isVerlegd ? '' : ' incl. BTW'}</span><span>{fmtEuro(quote.total)}</span></div>
              </div>
            </div>
            ); })()}

            {(quote.btw_status || '').toLowerCase() === 'verlegd' && (
              <div style={{ padding: '10px 14px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: '4px', fontSize: '12px', marginBottom: '15px', color: '#78350f' }}>
                <strong>BTW verlegd.</strong> Overeenkomstig artikel 12, vijfde lid van de Wet op de omzetbelasting 1968 is de heffing van btw verlegd naar de afnemer.
              </div>
            )}

            {quote.notes && <div style={{ padding: '15px', background: '#f8f9fa', borderRadius: '4px', fontSize: '12px', marginBottom: '20px', whiteSpace: 'pre-wrap' }}>{quote.notes}</div>}

            <PaymentPlanBlock plan={quote.payment_plan} totalIncl={quote.total} documentNumber={quote.number} documentType="Offerte" />
            <StandardTermsBlock />

            {quote.status === 'goedgekeurd' && quote.client_response && (
              <div style={{ marginTop: '20px', padding: '15px', background: '#ecfdf5', border: '1px solid #10b981', borderRadius: '4px', fontSize: '12px' }}>
                <p style={{ color: '#065f46', fontWeight: 'bold', marginBottom: '8px' }}>✓ Akkoord — Goedgekeurd door klant</p>
                <p><strong>Naam:</strong> {quote.client_response.signer_name}</p>
                {quote.client_response.signer_address && <p><strong>Adres:</strong> {quote.client_response.signer_address}</p>}
                {quote.client_response.signer_phone && <p><strong>Telefoon:</strong> {quote.client_response.signer_phone}</p>}
                <p><strong>Datum/tijd:</strong> {quote.client_response.responded_at && new Date(quote.client_response.responded_at).toLocaleString('nl-NL')}</p>
                {quote.client_response.signature_data && (
                  <div style={{ marginTop: '8px' }}>
                    <p style={{ color: '#666', fontSize: '11px', marginBottom: '4px' }}>Handtekening:</p>
                    <img src={quote.client_response.signature_data} alt="handtekening" style={{ maxHeight: '80px', background: 'white', border: '1px solid #ddd', borderRadius: '4px' }} />
                  </div>
                )}
              </div>
            )}
            {quote.status === 'afgewezen' && quote.client_response && (
              <div style={{ marginTop: '20px', padding: '15px', background: '#fef2f2', border: '1px solid #ef4444', borderRadius: '4px', fontSize: '12px' }}>
                <p style={{ color: '#991b1b', fontWeight: 'bold', marginBottom: '8px' }}>✗ Offerte afgewezen</p>
                {quote.client_response.rejection_reason && <p><strong>Reden:</strong> {quote.client_response.rejection_reason}</p>}
                <p><strong>Datum/tijd:</strong> {quote.client_response.responded_at && new Date(quote.client_response.responded_at).toLocaleString('nl-NL')}</p>
              </div>
            )}

            <div style={{ borderTop: '1px solid #ddd', paddingTop: '15px', fontSize: '11px', color: '#666', display: 'flex', justifyContent: 'space-between' }}>
              <div><p>{COMPANY.name} · {COMPANY.address}, {COMPANY.city}</p><p>Mobiel: {COMPANY.phone} · E-mail: {COMPANY.email}</p></div>
              <div style={{ textAlign: 'right' }}><p>KVK: {COMPANY.kvk} · BTW-id: {COMPANY.btw_id}</p><p>IBAN: {COMPANY.iban} · BIC: {COMPANY.bic}</p></div>
            </div>
          </div>
        </div>
        {/* end pdfRef */}

        {/* PDF download button + email-to-self (always visible — also before approval) */}
        <div className="flex flex-col sm:flex-row justify-end gap-2">
          <Button
            onClick={handleEmailToSelf}
            disabled={emailLoading || !quote.client_email}
            variant="outline"
            className="border-[#3da5a5] text-[#3da5a5] hover:bg-[#3da5a5]/5"
            title={quote.client_email ? `Stuurt naar ${quote.client_email}` : 'Geen e-mailadres bekend'}
            data-testid="email-quote-to-self"
          >
            {emailLoading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Versturen...</> : <><Mail className="w-4 h-4 mr-2" /> Stuur naar mijn e-mail</>}
          </Button>
          <Button onClick={handleDownloadPdf} variant="outline" className="border-[#1a5276] text-[#1a5276] hover:bg-[#1a5276]/5" data-testid="download-public-pdf">
            <Download className="w-4 h-4 mr-2" /> PDF downloaden
          </Button>
        </div>

        {/* Werkaanvraag-context + foto's + reacties */}
        {renderExtras()}

        {/* Approve form */}
        {mode === 'approve' && (
          <Card className="border-2 border-green-300" data-testid="approve-form">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-xl font-bold text-green-800 flex items-center gap-2">
                <Check className="w-5 h-5" /> Akkoord — Bevestig uw goedkeuring
              </h3>
              <p className="text-sm text-gray-600">Vul uw gegevens in en plaats uw handtekening om deze offerte juridisch te bevestigen.</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-gray-700">Volledige naam *</Label>
                  <Input value={signerName} onChange={e => setSignerName(e.target.value)} placeholder="Jan Jansen" data-testid="signer-name" />
                </div>
                <div>
                  <Label className="text-gray-700">Telefoonnummer *</Label>
                  <Input value={signerPhone} onChange={e => setSignerPhone(e.target.value)} placeholder="+31 6 12345678" data-testid="signer-phone" />
                </div>
                <div className="md:col-span-2">
                  <Label className="text-gray-700">Adres *</Label>
                  <Input value={signerAddress} onChange={e => setSignerAddress(e.target.value)} placeholder="Straat 1, Postcode Stad" data-testid="signer-address" />
                </div>
              </div>
              <div>
                <Label className="text-gray-700">Handtekening *</Label>
                <SignatureCanvas onChange={setSignatureData} />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setMode(null)} disabled={submitting} className="flex-1">Annuleren</Button>
                <Button onClick={handleApprove} disabled={submitting} className="flex-1 bg-green-600 hover:bg-green-700" data-testid="submit-approve">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig...</> : 'Bevestig akkoord'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Reject form */}
        {mode === 'reject' && (
          <Card className="border-2 border-orange-300" data-testid="reject-form">
            <CardContent className="p-6 space-y-4">
              <h3 className="text-xl font-bold text-orange-800 flex items-center gap-2">
                <X className="w-5 h-5" /> Offerte afwijzen
              </h3>
              <p className="text-sm text-orange-700 font-medium bg-orange-50 p-3 rounded">
                💬 We denken graag met u mee voor een aangepaste offerte!
              </p>
              <div>
                <Label className="text-gray-700">Reden (optioneel)</Label>
                <textarea
                  value={rejectionReason}
                  onChange={e => setRejectionReason(e.target.value)}
                  placeholder="Bijv. te duur, andere uitvoering gewenst, andere planning..."
                  className="w-full bg-gray-50 border border-gray-200 rounded-lg p-2 text-sm h-24"
                  data-testid="rejection-reason"
                />
              </div>
              <div className="flex gap-2 pt-2">
                <Button variant="outline" onClick={() => setMode(null)} disabled={submitting} className="flex-1">Annuleren</Button>
                <Button onClick={handleReject} disabled={submitting} className="flex-1 bg-orange-600 hover:bg-orange-700" data-testid="submit-reject">
                  {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig...</> : 'Offerte afwijzen'}
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Action buttons — hidden when the offerte has already been responded to */}
        {!mode && quote.status !== 'goedgekeurd' && quote.status !== 'afgewezen' && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Button onClick={() => setMode('approve')} className="bg-green-600 hover:bg-green-700 h-16 text-lg" data-testid="btn-approve">
              <Check className="w-6 h-6 mr-2" /> Akkoord — Ik ga akkoord
            </Button>
            <Button onClick={() => setMode('reject')} variant="outline" className="border-2 border-red-500 text-red-600 hover:bg-red-50 h-16 text-lg" data-testid="btn-reject">
              <X className="w-6 h-6 mr-2" /> Offerte afwijzen
            </Button>
          </div>
        )}

        <p className="text-xs text-gray-400 text-center pt-4 flex items-center justify-center gap-1">
          <FileText className="w-3 h-3" /> Cardoso Bouw en Onderhoud · Beveiligde link
        </p>
      </div>
    </div>
  );
};

export default PublicQuote;
